import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './MachineSchedules.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { toLocalDatetimeInput, localInputToUtc, toLocalDateKey, formatAppDate } from '../../utils/dateUtils';

const MachineSchedules = ({ machineId, editMode: parentEditMode, onEditModeChange }) => {
    const { t } = useTranslation('production');
    const [schedules, setSchedules] = useState([]);
    const [machines, setMachines] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const editMode = parentEditMode || false;
    const setEditMode = onEditModeChange || (() => {});
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [newSchedule, setNewSchedule] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [viewMode, setViewMode] = useState('calendar'); // 'list' or 'calendar'

    // Filters
    // Default date range: from a week ago to 30 days ahead
    const getDefaultDates = () => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(start.getDate() - 7);
        const end = new Date(today);
        end.setDate(end.getDate() + 30);
        return {
            startDate: toLocalDateKey(start),
            endDate: toLocalDateKey(end)
        };
    };

    const defaultDates = getDefaultDates();
    const [filters, setFilters] = useState({
        startDate: defaultDates.startDate,
        endDate: defaultDates.endDate,
        machineId: machineId || '',
        status: ''
    });

    // Status values from database enum
    const scheduleStatuses = [
        'SCHEDULED',
        'IN_PROGRESS',
        'COMPLETED',
        'CANCELLED',
        'ON_HOLD'
    ];

    const maintenanceTypes = [
        'Preventive',
        'Corrective',
        'Predictive',
        'Emergency',
        'Calibration',
        'Inspection'
    ];

    useEffect(() => {
        fetchMachines();
        fetchUsers();
        fetchSchedules();
    }, [filters]);

    // Handle ESC key
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                if (editingSchedule || newSchedule) {
                    handleCancelEdit();
                } else if (editMode) {
                    setEditMode(false);
                }
            }
        };

        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [editMode, editingSchedule, newSchedule]);

    // Set up window handlers for toolbar buttons
    useEffect(() => {
        window.newScheduleHandler = handleNewSchedule;
        window.scheduleSaveHandler = handleSave;

        return () => {
            delete window.newScheduleHandler;
            delete window.scheduleSaveHandler;
        };
    }, [saving, newSchedule, editingSchedule]);

    const fetchMachines = async () => {
        try {
            const response = await apiFetch(getApiUrl('/api/machines'));
            if (response.ok) {
                const data = await response.json();
                setMachines(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching machines:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await apiFetch(getApiUrl('/api/users/'));
            if (response.ok) {
                const data = await response.json();
                setUsers(Array.isArray(data) ? data : []);
            } else {
                console.error('Failed to fetch users:', response.status);
                setError(t('machines.schedules.messages.errorLoading'));
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setError(t('machines.schedules.messages.errorLoading'));
        }
    };

    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            // Filter dates are local days; the API compares UTC instants
            if (filters.startDate) params.append('start_date', localInputToUtc(`${filters.startDate}T00:00:00`));
            if (filters.endDate) params.append('end_date', localInputToUtc(`${filters.endDate}T23:59:59`));
            if (filters.machineId) params.append('machine_id', filters.machineId);
            if (filters.status) params.append('status', filters.status);

            const response = await apiFetch(getApiUrl(`/api/machine-schedules/?${params}`));
            if (response.ok) {
                const data = await response.json();
                setSchedules(Array.isArray(data) ? data : []);
            } else {
                setError(t('machines.schedules.messages.errorLoading'));
            }
        } catch (error) {
            console.error('Error fetching schedules:', error);
            setError(t('machines.schedules.messages.errorLoading'));
        } finally {
            setLoading(false);
        }
    };

    // datetime-local inputs hold local time; the API stores UTC
    const toApiSchedule = (schedule) => ({
        ...schedule,
        scheduled_start: localInputToUtc(schedule.scheduled_start),
        scheduled_end: localInputToUtc(schedule.scheduled_end),
        actual_start: localInputToUtc(schedule.actual_start),
        actual_end: localInputToUtc(schedule.actual_end)
    });

    const handleSave = async () => {
        if (saving) return;

        try {
            setSaving(true);
            setSaveSuccess(false);

            if (newSchedule) {
                // Validate machine_id
                if (!newSchedule.machine_id || newSchedule.machine_id === '') {
                    alert(t('machines.schedules.messages.selectMachineRequired'));
                    setSaving(false);
                    return;
                }

                // Create machine schedule
                const response = await apiFetch(
                    getApiUrl('/api/machine-schedules/'),
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(toApiSchedule(newSchedule))
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    const errorMessage = typeof errorData.detail === 'string'
                        ? errorData.detail
                        : (errorData.detail && typeof errorData.detail === 'object'
                            ? JSON.stringify(errorData.detail)
                            : (errorData.message || 'Failed to create schedule'));
                    throw new Error(errorMessage);
                }

                const result = await response.json();

                setSaveSuccess(true);
                setNewSchedule(null);
                setEditMode(false);
                fetchSchedules();

                setTimeout(() => setSaveSuccess(false), 3000);
            } else if (editingSchedule) {
                // Update existing machine schedule
                const response = await apiFetch(
                    getApiUrl(`/api/machine-schedules/${editingSchedule.schedule_id}`),
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(toApiSchedule(editingSchedule))
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || errorData.message || 'Failed to update schedule');
                }

                setSaveSuccess(true);
                setEditingSchedule(null);
                fetchSchedules();

                setTimeout(() => setSaveSuccess(false), 3000);
            }
        } catch (error) {
            const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
            alert(t('common:ui.errorWithMessage', { message: errorMessage }));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (scheduleId) => {
        if (!window.confirm(t('machines.schedules.messages.deleteConfirmation'))) return;

        try {
            const response = await apiFetch(
                getApiUrl(`/api/machine-schedules/${scheduleId}`),
                { method: 'DELETE' }
            );

            if (response.ok) {
                fetchSchedules();
            } else {
                alert(t('machines.schedules.messages.deleteFailed'));
            }
        } catch (error) {
            alert(t('common:ui.errorWithMessage', { message: error.message }));
        }
    };

    const handleEditSchedule = (schedule) => {
        setEditingSchedule({
            ...schedule,
            scheduled_start: toLocalDatetimeInput(schedule.scheduled_start),
            scheduled_end: toLocalDatetimeInput(schedule.scheduled_end),
            actual_start: toLocalDatetimeInput(schedule.actual_start),
            actual_end: toLocalDatetimeInput(schedule.actual_end)
        });
        setEditMode(true);
    };

    const handleNewSchedule = () => {
        const selectedMachineId = parseInt(filters.machineId) || parseInt(machineId) || '';
        const now = new Date();
        const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

        setNewSchedule({
            machine_id: selectedMachineId,
            production_order_id: null,
            scheduled_start: toLocalDatetimeInput(now.toISOString()),
            scheduled_end: toLocalDatetimeInput(tomorrow.toISOString()),
            actual_start: null,
            actual_end: null,
            status: 'SCHEDULED',
            notes: ''
        });
        setEditMode(true);
    };

    const handleCancelEdit = () => {
        setEditingSchedule(null);
        setNewSchedule(null);
        setEditMode(false);
    };

    const handleUpdateField = (field, value) => {
        if (editingSchedule) {
            setEditingSchedule({ ...editingSchedule, [field]: value });
        } else if (newSchedule) {
            setNewSchedule({ ...newSchedule, [field]: value });
        }
    };

    const getScheduleStatusColor = (status) => {
        const colors = {
            SCHEDULED: '#2196f3',
            IN_PROGRESS: '#ff9800',
            COMPLETED: '#4caf50',
            CANCELLED: '#f44336',
            ON_HOLD: '#607d8b'
        };
        return colors[status] || '#757575';
    };

    const generateCalendarGrid = () => {
        const start = new Date(`${filters.startDate}T00:00:00`);
        const end = new Date(`${filters.endDate}T00:00:00`);
        const calendar = [];

        // Start from the beginning of the week of the start date
        const currentDate = new Date(start);
        currentDate.setDate(currentDate.getDate() - currentDate.getDay()); // Go to Sunday

        // End at the end of the week of the end date
        const endDate = new Date(end);
        endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // Go to Saturday

        // Generate weeks
        while (currentDate <= endDate) {
            const week = [];
            for (let i = 0; i < 7; i++) {
                const date = new Date(currentDate);
                const dateStr = toLocalDateKey(date);

                // Find schedules for this date
                const daySchedules = schedules.filter(schedule =>
                    dateStr >= toLocalDateKey(schedule.scheduled_start) &&
                    dateStr <= toLocalDateKey(schedule.scheduled_end)
                );

                week.push({
                    date: date,
                    dateStr: dateStr,
                    schedules: daySchedules,
                    isInRange: date >= start && date <= end,
                    isToday: dateStr === toLocalDateKey(new Date())
                });

                currentDate.setDate(currentDate.getDate() + 1);
            }
            calendar.push(week);
        }

        return calendar;
    };

    const groupSchedulesByDate = () => {
        const grouped = {};
        schedules.forEach(schedule => {
            const startDate = new Date(`${toLocalDateKey(schedule.scheduled_start)}T00:00:00`);
            const endDate = new Date(`${toLocalDateKey(schedule.scheduled_end)}T00:00:00`);

            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                const dateStr = toLocalDateKey(d);
                if (!grouped[dateStr]) {
                    grouped[dateStr] = [];
                }

                const scheduleForDay = {
                    ...schedule,
                    is_multi_day: startDate.getTime() !== endDate.getTime(),
                    day_label: startDate.getTime() === endDate.getTime()
                        ? null
                        : `Day ${Math.floor((d - startDate) / (1000 * 60 * 60 * 24)) + 1} of ${Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1}`
                };
                grouped[dateStr].push(scheduleForDay);
            }
        });

        // Sort schedules within each date
        Object.keys(grouped).forEach(date => {
            grouped[date].sort((a, b) => {
                if (a.machine_code !== b.machine_code) {
                    return a.machine_code.localeCompare(b.machine_code);
                }
                const aTime = new Date(a.scheduled_start).getTime();
                const bTime = new Date(b.scheduled_start).getTime();
                return aTime - bTime;
            });
        });

        return grouped;
    };

    if (loading) {
        return <div className="machine-schedules-container">{t('machines.schedules.loading')}</div>;
    }

    if (error) {
        return (
            <div className="machine-schedules-container">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    const currentSchedule = editingSchedule || newSchedule;
    const groupedSchedules = groupSchedulesByDate();

    return (
        <div className="machine-schedules-container">
            {saveSuccess && (
                <div className="success-message" style={{ margin: '10px 20px' }}>{t('machines.schedules.messages.successSaved')}</div>
            )}

            {/* Filters Section */}
            <div className="schedule-filters">
                <h3>{t('machines.schedules.filters.title')}</h3>
                <div className="filter-grid">
                    <div className="filter-item">
                        <label>{t('machines.schedules.filters.startDate')}</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                            disabled={editMode && currentSchedule}
                        />
                    </div>
                    <div className="filter-item">
                        <label>{t('machines.schedules.filters.endDate')}</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                            disabled={editMode && currentSchedule}
                        />
                    </div>
                    <div className="filter-item">
                        <label>{t('machines.schedules.filters.machine')}</label>
                        <select
                            value={filters.machineId}
                            onChange={(e) => setFilters({ ...filters, machineId: e.target.value })}
                            disabled={editMode && currentSchedule}
                        >
                            <option value="">{t('machines.schedules.filters.allMachines')}</option>
                            {machines.map(machine => (
                                <option key={machine.machine_id} value={machine.machine_id}>
                                    {machine.machine_code} - {machine.machine_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>{t('machines.schedules.filters.status')}</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            disabled={editMode && currentSchedule}
                        >
                            <option value="">{t('machines.schedules.filters.allStatus')}</option>
                            {scheduleStatuses.map(status => (
                                <option key={status} value={status}>{t(`machines.schedules.statusDisplay.${status.toLowerCase()}`)}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Edit/New Schedule Form */}
            {editMode && currentSchedule && (
                <div className="edit-form-section">
                    <h3>{newSchedule ? t('machines.schedules.form.newSchedule') : t('machines.schedules.form.editSchedule')}</h3>
                    <div className="form-grid">
                        <div className="form-item">
                            <label>{t('machines.schedules.form.machine')} <span style={{color: 'red'}}>*</span></label>
                            <select
                                value={currentSchedule.machine_id}
                                onChange={(e) => handleUpdateField('machine_id', e.target.value ? parseInt(e.target.value) : '')}
                                required
                            >
                                <option value="" disabled hidden>{t('machines.schedules.form.selectMachine')}</option>
                                {machines.map(machine => (
                                    <option key={machine.machine_id} value={machine.machine_id}>
                                        {machine.machine_code} - {machine.machine_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-item">
                            <label>{t('machines.schedules.form.productionOrderId')}</label>
                            <input
                                type="number"
                                value={currentSchedule.production_order_id || ''}
                                onChange={(e) => handleUpdateField('production_order_id', e.target.value ? parseInt(e.target.value) : null)}
                                placeholder={t('machines.schedules.form.optional')}
                            />
                        </div>
                        <div className="form-item">
                            <label>{t('machines.schedules.form.scheduledStart')}</label>
                            <input
                                type="datetime-local"
                                value={currentSchedule.scheduled_start}
                                onChange={(e) => handleUpdateField('scheduled_start', e.target.value)}
                            />
                        </div>
                        <div className="form-item">
                            <label>{t('machines.schedules.form.scheduledEnd')}</label>
                            <input
                                type="datetime-local"
                                value={currentSchedule.scheduled_end}
                                onChange={(e) => handleUpdateField('scheduled_end', e.target.value)}
                                min={currentSchedule.scheduled_start}
                            />
                        </div>
                        <div className="form-item">
                            <label>{t('machines.schedules.form.actualStart')}</label>
                            <input
                                type="datetime-local"
                                value={currentSchedule.actual_start || ''}
                                onChange={(e) => handleUpdateField('actual_start', e.target.value || null)}
                            />
                        </div>
                        <div className="form-item">
                            <label>{t('machines.schedules.form.actualEnd')}</label>
                            <input
                                type="datetime-local"
                                value={currentSchedule.actual_end || ''}
                                onChange={(e) => handleUpdateField('actual_end', e.target.value || null)}
                            />
                        </div>
                        <div className="form-item">
                            <label>{t('machines.schedules.form.status')}</label>
                            <select
                                value={currentSchedule.status}
                                onChange={(e) => handleUpdateField('status', e.target.value)}
                            >
                                {scheduleStatuses.map(status => (
                                    <option key={status} value={status}>{t(`machines.schedules.statusDisplay.${status.toLowerCase()}`)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="form-item form-item-full">
                        <label>{t('machines.schedules.form.notes')}</label>
                        <textarea
                            value={currentSchedule.notes || ''}
                            onChange={(e) => handleUpdateField('notes', e.target.value)}
                            rows="3"
                        />
                    </div>
                </div>
            )}

            {/* Schedules List */}
            <div className="schedule-calendar-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', marginTop: '10px' }}>
                    <h3 style={{ color: 'var(--primary)', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', margin: 0 }}>
                        {t('machines.schedules.views.scheduleCalendar')}
                    </h3>
                    <div className="view-toggle-buttons">
                        <button
                            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            style={{
                                padding: '6px 12px',
                                marginRight: '5px',
                                backgroundColor: viewMode === 'list' ? '#007bff' : '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            {t('machines.schedules.views.list')}
                        </button>
                        <button
                            className={`view-toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                            onClick={() => setViewMode('calendar')}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: viewMode === 'calendar' ? '#007bff' : '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            {t('machines.schedules.views.calendar')}
                        </button>
                    </div>
                </div>
                {schedules.length === 0 ? (
                    <div className="empty-state">
                        <p>{t('machines.schedules.messages.noSchedulesFound')}</p>
                    </div>
                ) : viewMode === 'calendar' ? (
                    // Calendar View
                    <div className="calendar-view" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#1a1a1a' }}>
                            <thead>
                                <tr>
                                    {[
                                        t('machines.schedules.calendar.days.sun'),
                                        t('machines.schedules.calendar.days.mon'),
                                        t('machines.schedules.calendar.days.tue'),
                                        t('machines.schedules.calendar.days.wed'),
                                        t('machines.schedules.calendar.days.thu'),
                                        t('machines.schedules.calendar.days.fri'),
                                        t('machines.schedules.calendar.days.sat')
                                    ].map((day, index) => (
                                        <th key={index} style={{
                                            padding: '10px',
                                            backgroundColor: '#2a2a2a',
                                            color: '#ffffff',
                                            borderBottom: '2px solid #007bff',
                                            textAlign: 'center',
                                            width: '14.28%'
                                        }}>
                                            {day}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {generateCalendarGrid().map((week, weekIndex) => (
                                    <tr key={weekIndex}>
                                        {week.map((day, dayIndex) => (
                                            <td key={dayIndex} style={{
                                                padding: '5px',
                                                verticalAlign: 'top',
                                                border: '1px solid #333',
                                                backgroundColor: day.isToday ? '#1a3a52' : (day.isInRange ? '#1e1e1e' : '#141414'),
                                                minHeight: '100px',
                                                height: '120px',
                                                position: 'relative'
                                            }}>
                                                <div style={{
                                                    fontWeight: 'bold',
                                                    marginBottom: '5px',
                                                    color: day.isToday ? '#4dabf7' : (day.isInRange ? '#ffffff' : '#666'),
                                                    fontSize: '14px'
                                                }}>
                                                    {day.date.getDate()}
                                                </div>
                                                <div style={{ fontSize: '11px', overflow: 'auto', maxHeight: '80px' }}>
                                                    {day.schedules.map((schedule, idx) => (
                                                        <div key={idx} style={{
                                                            backgroundColor: getScheduleStatusColor(schedule.status),
                                                            color: 'white',
                                                            padding: '2px 4px',
                                                            marginBottom: '2px',
                                                            borderRadius: '3px',
                                                            fontSize: '10px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={`${schedule.machine_code} - ${schedule.status}\n${formatAppDate(schedule.scheduled_start, 'time')} - ${formatAppDate(schedule.scheduled_end, 'time')}`}
                                                        onClick={() => editMode && !currentSchedule && handleEditSchedule(schedule)}
                                                        >
                                                            <strong>{formatAppDate(schedule.scheduled_start, 'time')}</strong> {schedule.machine_code}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    // List View
                    Object.keys(groupedSchedules).sort().map(date => (
                        <div key={date} className="schedule-date-group">
                            <div className="schedule-date-header">
                                <h4>
                                    {formatAppDate(date, {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </h4>
                            </div>
                            <table className="schedule-table">
                                <thead>
                                    <tr>
                                        <th>{t('machines.schedules.table.headers.scheduledTime')}</th>
                                        <th>{t('machines.schedules.table.headers.machine')}</th>
                                        <th>{t('machines.schedules.table.headers.productionOrder')}</th>
                                        <th>{t('machines.schedules.table.headers.status')}</th>
                                        <th>{t('machines.schedules.table.headers.actualTime')}</th>
                                        <th>{t('machines.schedules.table.headers.notes')}</th>
                                        {editMode && !currentSchedule && <th>{t('machines.schedules.table.headers.actions')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedSchedules[date].map(schedule => (
                                        <tr key={schedule.schedule_id}>
                                            <td>
                                                {formatAppDate(schedule.scheduled_start, 'time')} - {formatAppDate(schedule.scheduled_end, 'time')}
                                            </td>
                                            <td>{schedule.machine_code} - {schedule.machine_name}</td>
                                            <td>{schedule.po_number || schedule.production_order_id || '-'}</td>
                                            <td>
                                                <span
                                                    className="schedule-status-badge"
                                                    style={{ backgroundColor: getScheduleStatusColor(schedule.status), color: 'white', padding: '2px 6px', borderRadius: '3px' }}
                                                >
                                                    {t(`machines.schedules.statusDisplay.${schedule.status.toLowerCase()}`)}
                                                </span>
                                            </td>
                                            <td>
                                                {schedule.actual_start && schedule.actual_end ? (
                                                    `${formatAppDate(schedule.actual_start, 'time')} - ${formatAppDate(schedule.actual_end, 'time')}`
                                                ) : schedule.actual_start ? (
                                                    `${t('machines.schedules.messages.started')} ${formatAppDate(schedule.actual_start, 'time')}`
                                                ) : '-'}
                                            </td>
                                            <td>
                                                {schedule.day_label && (
                                                    <span style={{ color: '#007bff', fontSize: '0.85em', marginRight: '8px' }}>
                                                        [{schedule.day_label}]
                                                    </span>
                                                )}
                                                {schedule.notes || '-'}
                                            </td>
                                            {editMode && !currentSchedule && (
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            onClick={() => handleEditSchedule(schedule)}
                                                            className="btn btn-secondary"
                                                            style={{ padding: '3px 6px', fontSize: '11px' }}
                                                        >
                                                            {t('machines.schedules.buttons.edit')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(schedule.schedule_id)}
                                                            className="btn btn-danger"
                                                            style={{ padding: '3px 6px', fontSize: '11px' }}
                                                        >
                                                            {t('machines.schedules.buttons.delete')}
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default MachineSchedules;